import * as React from "react"

import { cn } from "../../cn"

/**
 * The invisible strip a pointer drags. Wider than the border it straddles so
 * the target is comfortable, and marked `group` so the grip can react to hover,
 * focus, and an in-progress drag.
 */
export const resizeHandleClassName =
  "group absolute top-0 z-10 h-full w-3 cursor-col-resize bg-transparent"

/** Wrapper spanning the handle, so the parts below can centre on its border. */
const gripBoxClassName = "pointer-events-none absolute inset-0"

/**
 * The border between the two panes, redrawn on top of the pane's own so it can
 * darken while the divider is live without the pane having to know about it.
 */
const gripLineClassName = cn(
  "absolute inset-y-0 left-1/2 z-20 w-px bg-transparent",
  "transition-colors duration-150",
  "group-hover:bg-coval-border-strong",
  "group-focus-visible:bg-coval-border-strong",
  "group-data-[resizing=true]:bg-coval-border-strong",
)

/**
 * The grabber: a pill riding the middle of the border between two panes, in the
 * same tone the border takes while the divider is live. It stays hidden until
 * the divider is hovered, focused, or being dragged, so a resting page shows
 * nothing but the border itself.
 */
const gripPillClassName = cn(
  // The line it rides is the 1px starting at the midpoint, so the pill centres
  // half a pixel further right to sit concentric with it.
  "absolute left-[calc(50%+0.5px)] top-1/2 z-[21] h-[36px] w-[5px]",
  "-translate-x-1/2 -translate-y-1/2",
  "rounded-[2.5px] bg-coval-border-strong",
  "opacity-0 transition-opacity duration-150",
  "group-hover:opacity-100 group-focus-visible:opacity-100",
  "group-data-[resizing=true]:opacity-100",
)

export type ResizeGripProps = React.HTMLAttributes<HTMLSpanElement>

/**
 * Affordance for a drag-to-resize divider: a border that darkens and a grabber
 * that appears while the divider is live. Render it inside the drag handle —
 * the handle owns the pointer behaviour, this only draws the affordance.
 */
const ResizeGrip = React.forwardRef<HTMLSpanElement, ResizeGripProps>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      aria-hidden
      className={cn(gripBoxClassName, className)}
      {...props}
    >
      <span className={gripLineClassName} />
      <span className={gripPillClassName} />
    </span>
  ),
)

ResizeGrip.displayName = "ResizeGrip"

/**
 * Imperative equivalent of `<ResizeGrip />`, for handles built as raw DOM —
 * react-split hands us a bare element to fill in rather than a React tree.
 */
export function createResizeGrip(): HTMLSpanElement {
  const box = document.createElement("span")
  box.className = gripBoxClassName
  box.setAttribute("aria-hidden", "true")

  for (const partClassName of [gripLineClassName, gripPillClassName]) {
    const part = document.createElement("span")
    part.className = partClassName
    box.appendChild(part)
  }

  return box
}

export { ResizeGrip }
